<template>
  <Modal name="abilityCheck">
    <template v-slot:default="{ options }">
      <ModalCard>
        <template v-slot:title>{{ options.skill?.name || ability.toUpperCase() }} Check</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <DiceRoll ref="diceRoller" :roll="roll" :character="character" :computed="computed" />
          </div>
        </div>

        <div class="field has-addons has-addons-centered">
          <div v-if="!appState.hardRolls" class="control">
            <a class="button is-primary" @click="rollDice">Roll</a>
          </div>
          <div v-if="bonuses.inspiration" class="control">
            <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
          </div>
        </div>
      </ModalCard>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, ref, watch, inject } from 'vue'

import useModal from '@/hooks/useModal'

import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'
import DiceRoll from '@/components/DiceRoll'

import skillList from '@/data/skills'

export default defineComponent({
  components: {
    DiceRoll,
    Modal,
    ModalCard
  },

  setup (props, { emit }) {
    const character = inject('character')
    const computed = inject('computedCharacter')
    const appState = inject('appState')

    const { currentModal, openModal } = useModal()
    
    const ability = ref(null)
    const useInspiration = ref(false)
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'abilityCheck') {
        useInspiration.value = false
        return
      }

      ability.value = modal.options.key
        ? skillList[modal.options.key].ability
        : modal.options.savingThrow
    })
    
    const roll = ref(null)
    const makeRoll = (modal, inspired) => {
      if (modal === null || modal.name !== 'abilityCheck') {
        return roll.value = []
      }
      const parts = [ `+1d20`, `+${ability.value}` ]
      if (modal.options.key && character.skills.includes(modal.options.key)) {
        parts.push(`+prof`)
      } else if (modal.options.savingThrow && computed.characterClass.value.savingThrows.includes(ability.value)) {
        parts.push(`+prof`)
      }
      if (computed.bonuses.value.allAbilities) {
        parts.push(`+${computed.bonuses.value.allAbilities}`)
      }
      if (inspired && computed.bonuses.value.inspiration) {
        parts.push('+1')
      }
      roll.value = parts
    }
    watch(currentModal, (modal) => makeRoll(modal, useInspiration.value))
    watch(useInspiration, (inspired) => {
      makeRoll(currentModal.value, inspired)

      if (currentModal.value === null || currentModal.value.name !== 'abilityCheck') {
        return
      }

      if (appState.hardRolls) {
        if (inspired && computed.bonuses.value.inspiration) {
          currentModal.value.options.onClose = () => {
            emit('removeInspiration')
          }
        } else {
          delete currentModal.value.options.onClose
        }
      }
    })

    const diceRoller = ref(null)
    const rollDice = () => {
      diceRoller.value.rollDice()
      
      if (useInspiration.value && computed.bonuses.value.inspiration) {
        currentModal.value.options.onClose = () => {
          emit('removeInspiration')
        }
      } else {
        delete currentModal.value.options.onClose
      }
    }

    return {
      appState,
      character,
      computed,
      ...computed,
      roll,
      openModal,
      ability,
      diceRoller,
      rollDice,
      useInspiration
    }
  }
})
</script>

<style lang="sass" scoped>
.spellSaveControl
  padding: 1em 0

.spellSave
  font-family: 'Playfair Display', sans-serif
  text-align: center
  margin: 0 0.25em
  margin-bottom: 0 !important
  text-transform: uppercase
</style>
